class Carousel extends HTMLElement {
  connectedCallback() {
    if(this.dataset.carouselEnabled === 'false') {
      return;
    }
    this.carouselId = this.dataset.carouselId;
    if(!this.carouselId) {
      throw new Error('Carousel requires a valid ID.');
    }
    if(this.hasAttribute('data-carousel-duration')) {
      this.duration = parseFloat(this.dataset.carouselDuration);
    }
    const wait = this.dataset.carouselWait ?? false;
    if(wait) {
      setTimeout(() => {
        this.initiate();
      }, parseInt(wait))
    }
    else {
      this.initiate();
    }
  }

  initiate() {
    this.container = this.querySelector(this.dataset.carouselContainer) ?? this;
    this.items = this.container.children;
    Array.from(this.items).forEach(i => i.classList.add('carousel-block'))
    if(this.items.length === 0) {
      throw new Error(`Carousel requires at least one item. ${this.carouselId}`)
    }
    this.animate = horizontalLoop(this.items, {paused: true, disable_resize: true});
    this.paused = false;
    const startPosition = this.dataset.carouselStart ?? 0;
    if(startPosition > 0) {
      this.jumpTo(startPosition, 0);
    }
    const jumpTriggers = document.querySelectorAll(`[data-carousel-jump="${this.carouselId}"]`);
    jumpTriggers.forEach(trigger => {
      trigger.addEventListener('click', event => {
        event.preventDefault();
        const index = trigger.dataset.jumpTo;
        this.jumpTo(index);
        document.querySelectorAll(`[data-carousel-jump][data-state=active]`).forEach(i => {
          i.setAttribute('data-state', 'inactive');
        })
        trigger.setAttribute('data-state', 'active');
      })
    })
    this.controls = document.querySelector(`carousel-controls[data-carousel-id="${this.carouselId}"]`);

    this.teardownSwipe = setupHorizontalSwipe(this, {
      onSwipeNext: () => this.nextItem(),
      onSwipePrev: () => this.prevItem()
    });
    this.onResize = debounce(() => this.handleResize(), 300);
    window.addEventListener('resize', this.onResize);

    if(this.controls) {
      this.slideSpeed = parseInt(this.controls.dataset.carouselSpeed ?? 100);
      this.duration = this.duration ?? parseFloat(this.controls.dataset.carouselDuration ?? 1.5);
      this.autoSlide = this.controls.hasAttribute('data-carousel-auto')
        && this.controls.dataset.carouselAuto !== 'false';
      this.carouselPause = this.controls.hasAttribute('data-carousel-pause');
      this.looping = this.slideSpeed === 0 && this.autoSlide;
      this.setup();
      if(!Shopify.designMode && this.autoSlide) {
        this.observeInViewport();
      }

      // Stop the scrolling if the user changes tab. This prevents the script from playing catchup if they return to the tab.
      this.onVisibilityChange = this.stopAutoScroll.bind(this);
      document.addEventListener("visibilitychange", this.onVisibilityChange);
    }
  }

  disconnectedCallback() {
    this.interval && clearInterval(this.interval);
    this.pauseInterval && clearInterval(this.pauseInterval);
    this.observer && this.observer.disconnect();
    this.teardownSwipe && this.teardownSwipe();
    this.onResize && window.removeEventListener('resize', this.onResize);
    this.onVisibilityChange && document.removeEventListener("visibilitychange", this.onVisibilityChange);
  }

  definePauseListener() {
    this.pauseListener = debounce(() => {
      if(!this.matches(':hover')) {
        return;
      }
      this.removeEventListener('mouseover', this.pauseListener);
      this.animate.pause();
      this.pauseInterval = setInterval(() => {
        if (!this.matches(':hover')) {
          this.startAutoScroll();
          if(this.carouselPause) {
            this.definePauseListener();
          }
        }
      }, 2000)
    }, 1000)
    this.registerPauseListener();
  }

  registerPauseListener() {
    this.addEventListener('mouseover', this.pauseListener)
  }

  setup() {
    if(this.looping) {
      this.animate = horizontalLoop(this.items, {paused: true, repeat: -1, speed: this.duration === 0 ? 1 : this.duration});
      this.hideControls();
      if(this.carouselPause) {
        this.definePauseListener();
      }
      return;
    }
    const controlType = this.controls.dataset.carouselType;
    if(controlType === 'arrow') {
      this.controls.querySelector('carousel-prev-arrow')
        ?.addEventListener('click', this.prevItem.bind(this));
      this.controls.querySelector('carousel-next-arrow')
        ?.addEventListener('click', this.nextItem.bind(this));
    }
    else if(controlType === 'counters') {
      const template = this.controls.querySelector('carousel-counter').cloneNode();
      const holder = this.controls.querySelector('carousel-counter-holder');
      holder.innerHTML = '';
      for (let i = 0; i < this.items.length; i++) {
        const newNode = template.cloneNode();
        newNode.setAttribute('data-carousel-index', `${i}`);
        if(i === 0) {
          newNode.setAttribute('data-state', 'active')
        }
        holder.append(newNode)
        newNode.addEventListener('click', this.handleClickCounter.bind(this))
      }
    }
    else if(controlType === 'gallery') {
      this.controls.querySelector('carousel-prev-arrow')
        ?.addEventListener('click', this.prevGalleryItem.bind(this));
      this.controls.querySelector('carousel-next-arrow')
        ?.addEventListener('click', this.nextGalleryItem.bind(this));
    }
    this.toggleControls();
  }

  handleClickCounter(event) {
    const index = event.target.dataset.carouselIndex;
    this.jumpTo(index, 0)
  }

  activateCounter() {
    this.updateGalleryCounter();
    const active = this.controls?.querySelectorAll('carousel-counter[data-state=active]') ?? [];
    active.forEach(i => i.setAttribute('data-state', 'inactive'));
    this.controls?.querySelector(`carousel-counter[data-carousel-index="${this.animate.current()}"]`)
      ?.setAttribute('data-state', 'active');
  }

  handleResize() {
    if(this.controls) {
      this.toggleControls();
    }
  }

  isVisible(element) {
    const rect = element.getBoundingClientRect();
    const viewport = this.getBoundingClientRect();
    
    return rect.right >= viewport.left && rect.right <= viewport.right; 
  }

  toggleControls() {
    if(!this.controls) {
      return false;
    }
    let visible = false;
    for (const i of this.items) {
      if(!this.isVisible(i)) {
        visible = true;
        break;
      }
    }
    visible ? this.showControls() : this.hideControls();
  }

  showControls() {
    this.controls.style.display = 'flex';
    setTimeout(() => {
      this.controls.style.opacity = 1;
    }, 100)
  }

  hideControls() {
    this.controls.style.opacity = 0;
    setTimeout(() => {
      this.controls.style.display = 'none';
    }, 150)
  }

  observeInViewport() {
    watchViewportEnter(this, () => this.startAutoScroll(), { skipInDesignMode: true });
  }

  observeLeavesViewport() {
    watchViewportLeave(this, () => this.stopAutoScroll());
  }

  startAutoScroll() {
    if(this.looping) {
      this.animate.play();
    }
    else {
      this.setAutoInterval();
    }
    this.observeLeavesViewport();
  }

  stopAutoScroll() {
    this.interval && clearInterval(this.interval);
    if(this.looping) {
      this.animate.pause();
    }
    this.observeInViewport();
  }

  resetAutoInterval() {
    this.interval && clearInterval(this.interval);
    this.setAutoInterval();
  }

  setAutoInterval() {
    if(this.autoSlide) {
      if(!Shopify.designMode) {
        this.interval = setInterval(() => {
          if(this.carouselPause) {
            if (this.matches(':hover')) return;
          }
          for (const i of this.items) {
            if(!this.isVisible(i)) {
              this.nextItem();
              break;
            }
          }
        }, this.slideSpeed * 1000)
      }
    }
  }

  dispatchChangeEvent() {
    if(this.slideSpeed > 0 || !this.autoSlide) {
      this.dispatchEvent(
        new CustomEvent('slide', {
          bubbles: true,
          detail: {
            currentSlide: this.animate.current()
          }
        })
      )
    }
  }

  nextItem() {
    pauseAllMedia(this);
    this.resetAutoInterval();
    this.animate.next({duration: this.duration ?? 1.5, ease: "power1.inOut"});
    this.activateCounter();
    this.dispatchChangeEvent();
  }

  prevItem() {
    pauseAllMedia(this);
    this.resetAutoInterval();
    this.animate.previous({duration: this.duration ?? 1.5, ease: "power1.inOut"});
    this.activateCounter();
    this.dispatchChangeEvent();
  }

  nextGalleryItem() {
    this.nextItem();
  }

  prevGalleryItem() {
    this.prevItem();
  }

  updateGalleryCounter() {
    if(this.controls?.dataset?.carouselType === 'gallery') {
      const index = this.controls?.querySelector('carousel-index');
      index.textContent = `${this.animate.current() + 1}`
    }
  }

  jumpTo(index, customDuration = null) {
    pauseAllMedia(this);
    this.resetAutoInterval();
    this.animate.toIndex(parseInt(index), {duration: customDuration ?? this.duration ?? 1.5, ease: "power1.inOut"})
    this.activateCounter();
    this.dispatchChangeEvent();
  }

}

customElements.define('carousel-block', Carousel);

// Arrow controls for native scroll-snap carousels (e.g. the mobile-only carousel when the JS carousel is disabled).
class ScrollCarouselControls extends HTMLElement {
  connectedCallback() {
    this.track = document.querySelector(`[data-scroll-carousel-id="${this.dataset.carouselId}"]`);
    if(!this.track) {
      return;
    }
    this.prev = this.querySelector('[data-scroll-carousel-prev]');
    this.next = this.querySelector('[data-scroll-carousel-next]');
    this.onPrev = () => this.scrollByItem(-1);
    this.onNext = () => this.scrollByItem(1);
    this.onUpdate = debounce(() => this.updateState(), 50);
    this.prev?.addEventListener('click', this.onPrev);
    this.next?.addEventListener('click', this.onNext);
    window.addEventListener('resize', this.onUpdate);
    this.updateState();
  }

  disconnectedCallback() {
    this.prev?.removeEventListener('click', this.onPrev);
    this.next?.removeEventListener('click', this.onNext);
    this.onUpdate && window.removeEventListener('resize', this.onUpdate);
  }

  scrollByItem(direction) {
    const item = this.track.firstElementChild;
    if(!item) {
      return;
    }
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    // Loop around when at either end.
    if(direction < 0 && this.track.scrollLeft <= 1) {
      this.track.scrollTo({ left: maxScroll, behavior: 'smooth' });
      return;
    }
    if(direction > 0 && this.track.scrollLeft >= maxScroll - 1) {
      this.track.scrollTo({ left: 0, behavior: 'smooth' });
      return;
    }
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    this.track.scrollBy({ left: direction * (item.getBoundingClientRect().width + gap), behavior: 'smooth' });
  }

  updateState() {
    this.hidden = this.track.scrollWidth - this.track.clientWidth <= 1;
  }
}

customElements.define('scroll-carousel-controls', ScrollCarouselControls);

class ScrollingText extends HTMLElement {
  connectedCallback() {
    this.scrollSpeed = parseFloat(this.dataset.scrollSpeed) ?? 1.5;
    this.reversed = this.dataset.scrollReverse === 'true';
    this.items = this.children;
    if(this.items.length === 0) {
      throw new Error(`Scrolling text requires at least one item. ${this.carouselId}`)
    }
    this.fillScroll();
    setTimeout(() => {
      this.setupScroll();
    }, 2000) 
  }

  fillScroll() {
    this.sectionWidth = this.getBoundingClientRect().width;
    const size = this.items.length;
    while(this.sectionWidth < (window.innerWidth * 1.5)) {
      for (let i = 0; i < size; i++) {
        const newItem = this.items[i].cloneNode();
        newItem.innerHTML = this.items[i].innerHTML;
        newItem.removeAttribute('id');
        this.append(newItem)
      }
      this.sectionWidth = this.getBoundingClientRect().width;
    }
  }

  setupScroll() {
    this.loop = horizontalLoop(this.items, {paused: true, repeat: -1, speed: this.scrollSpeed});
    this.observeInViewport();
    // Stop the scrolling if the user changes tab. This prevents the script from playing catchup if they return to the tab.
    this.onVisibilityChange = this.stopAutoScroll.bind(this);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  }

  disconnectedCallback() {
    this.interval && clearInterval(this.interval);
    this.observer && this.observer.disconnect();
    this.onVisibilityChange && document.removeEventListener("visibilitychange", this.onVisibilityChange);
  }

  stopAutoScroll() {
    this.interval && clearInterval(this.interval);
    this.loop.pause();
    this.observeInViewport();
  }

  startAutoScroll() {
    this.observeLeavesViewport();
    if(this.reversed) {
      this.loop.vars.onReverseComplete();
      this.loop.reverse();
    }
    else {
      this.loop.play();
    }
  }

  observeInViewport() {
    watchViewportEnter(this, () => this.startAutoScroll());
  }

  observeLeavesViewport() {
    watchViewportLeave(this, () => this.stopAutoScroll());
  }
}

customElements.define('scrolling-text', ScrollingText)

class CarouselBar extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.thumb = this.querySelector('[data-carousel-thumb]');
    this.id = this.dataset.carouselId;
    this.carousel = document.querySelector(`carousel-block[data-carousel-id=${this.id}]`).parentNode;
    if(!this.carousel) {
      return;
    }
    this.calcDimensions();
    if(this.carouselWidth > this.pageWidth) {
      this.enableDrag();
      window.addEventListener('resize', this.handleResize.bind(this))
      this.carousel.addEventListener('touchstart', this.handleTouchstart.bind(this), { passive: true })
      this.carousel.addEventListener('touchmove', this.handleTouchmove.bind(this), { passive: false })
      this.carousel.addEventListener('touchend', this.handleTouchend.bind(this), { passive: true })
      this.track.addEventListener('click', this.handleClickBar.bind(this));
      // this.carousel.addEventListener('touchcancel', this.handleTouchend.bind(this), { passive: false })
    }
  }

  calcDimensions() {
    this.trackWidth = this.track.getBoundingClientRect().width;
    this.carouselWidth = this.carousel.scrollWidth;
    this.pageWidth = this.carousel.getBoundingClientRect().width;
    this.thumbWidth = this.thumb.getBoundingClientRect().width;
    this.slideWidth = this.trackWidth - this.thumbWidth;
    if(this.carouselWidth > this.pageWidth) {
      this.classList.remove('hidden')
    }
    else {
      this.classList.add('hidden')
    }
  }

  enableDrag() {
    if(this.draggableController) this.draggableController[0].kill();
    this.draggableController = Draggable.create(this.thumb, {
      trigger: [this.thumb],
      type: 'x',
      bounds: {minX: 0, maxX: this.slideWidth},
      inertia: true,
      autoScroll: 1,
      onPress: () => this.thumb.dataset.state = 'active',
      onRelease: () => delete this.thumb.dataset.state,
      onDrag: (e) => {
        const ratio = (this.carouselWidth - this.pageWidth) / (this.slideWidth);
        const progress = this.draggableController[0].x * ratio;
        this.carousel.scrollTo({
          top: 0,
          left: progress
        })
      }
    })
  }

  handleResize() {
    this.calcDimensions();
    this.enableDrag();
  }

  handleClickBar(e) {
    const x = e.offsetX < this.thumb.style.left
      ? e.offsetX + (this.thumbWidth / 2)
      : e.offsetX - (this.thumbWidth / 2)
    const scrollX = x < 0 ? 0 : (x > this.slideWidth ? this.slideWidth : x);
    const ratio = (this.carouselWidth - this.pageWidth) / (this.slideWidth);
    const progress = scrollX * ratio;
    gsap.to(this.thumb, {
      x: scrollX,
      duration: 0.2
    })
    gsap.to(this.carousel, {
      scrollTo: {
        x: progress
      },
      duration: 0.4
    })
  }

  handleTouchstart(e) {
    const evt = (typeof e.originalEvent === 'undefined') ? e : e.originalEvent;
    const touch = evt.touches[0] || evt.changedTouches[0];
    this.screenX = touch.screenX;
    this.carouselScrollStart = this.carousel.scrollLeft;
    this.thumbScrollStart = this.draggableController[0].startX;
  }

  handleTouchmove(e) {
    const ratio = (this.slideWidth) / (this.carouselWidth - this.pageWidth);
    const thumbX = this.carousel.scrollLeft * ratio;
    gsap.to(this.thumb, {
      x: thumbX < 0 ? 0 : (thumbX > this.slideWidth ? this.slideWidth : thumbX),
      duration: 0.4
    })
  }

  handleTouchend(e) {
    setTimeout(() => {
      const ratio = (this.slideWidth) / (this.carouselWidth - this.pageWidth);
      const thumbX = this.carousel.scrollLeft * ratio;
      gsap.to(this.thumb, {
        x: thumbX < 0 ? 0 : (thumbX > this.slideWidth ? this.slideWidth : thumbX),
        duration: 0.4
      })
    }, 300)
  }
}

customElements.define('carousel-bar', CarouselBar);